import { useRouter } from "next/router";
import { useChuseokOff } from "@/hooks/useChuseokOff";
import { customEvent } from "goi_common";

import { REGION_INDEX } from "@/constants/regionIndex";

/**
 * 헤더 Nav. 이 서브도메인의 색인 표면 셋과 비용 퍼널 하나로 둔다.
 *
 * 장지를 봉안당, 자연장, 묘지로 쪼갠 이유: 네이버에서 실제로 검색되는 말이
 * "장지" 가 아니라 "납골당", "수목장", "공원묘지" 다.
 *
 * 화장장은 장지가 아니라 별도 표면이다. 원본 데이터(hwajang_detail.json)와
 * 요금 체계(관내, 인근, 관외)가 장지와 다르고 검색어도 갈린다.
 * /region/facility/ 전체 인덱스는 세 유형의 상위 표면으로 남기고 Nav 에는 두지 않는다.
 *
 * 전국 시설을 한 페이지에서 필터하는 형태로 만들지 않은 이유: 검색 결과가
 * 클라이언트 상태라 URL 이 하나뿐이어서 색인되지 않는다. 지역별 페이지가
 * 이 도메인의 색인 단위이므로 Nav 도 그 인덱스로 보낸다.
 *
 * 홈(GoiHeader)과 나머지 페이지(ParlorHeader)가 같이 쓴다.
 * 스타일은 parlor-detail.css 의 pd-header__nav 이고 _app.tsx 에서 전역 import 된다.
 */
const NAV_ITEMS = [
  {
    // 첫 자리. 색인 표면 셋보다 이 도메인이 전환시켜야 하는 것이 먼저다.
    // 크로스도메인으로 내보내지 않고 이 도메인의 견적 LP 로 보낸다
    label: "장례비용",
    href: "/quote/",
    trigger: "nav_hdr_cost",
    // www 헤더의 "상품안내 N" 과 같은 표시. 이 항목만 전환 퍼널이라 눈에 걸리게 둔다
    badge: "N",
  },
  {
    label: "장례식장",
    href: REGION_INDEX.parlor.href,
    trigger: "nav_hdr_parlor",
  },
  {
    // 장례식장 다음 자리. 장례 순서(빈소 -> 화장 -> 장지)를 그대로 따른다.
    // 화장장은 장지 세 유형과 달리 데이터 원본과 요금 체계가 별개다
    label: "화장장",
    href: REGION_INDEX.crematorium.href,
    trigger: "nav_hdr_crematorium",
  },
  {
    label: "봉안당",
    href: REGION_INDEX.charnel.href,
    trigger: "nav_hdr_charnel",
  },
  {
    label: "자연장",
    href: REGION_INDEX.natural.href,
    trigger: "nav_hdr_natural",
  },
  {
    label: "묘지",
    href: REGION_INDEX.cemetery.href,
    trigger: "nav_hdr_cemetery",
  },
] as const;

/** Nav 행 높이. 헤더 아래 sticky 요소의 오프셋 계산에 쓴다 */
export const HEADER_NAV_HEIGHT = 44;

interface HeaderNavProps {
  /**
   * 홈(GoiHeader)은 로고 행의 폭과 여백이 다르다(모바일 480px 캡, lg 좌우 120px).
   * 좌측을 맞추려면 Nav 도 같은 값을 써야 해서 변형을 둔다.
   */
  variant?: "default" | "home";
}

export const HeaderNav = ({ variant = "default" }: HeaderNavProps) => {
  const router = useRouter();

  /**
   * 2026 추석. 이 Nav 의 장례식장, 화장장, 봉안당, 자연장, 묘지가 곧 시설탐색이고,
   * 그 끝이 연휴에 콜을 받을 수 없는 장지, 화장장 페이지다. 탐색 입구를 닫고
   * 전환 퍼널인 장례비용만 남긴다.
   */
  const isChuseokOff = useChuseokOff();
  const navItems = isChuseokOff
    ? NAV_ITEMS.filter((item) => item.href === "/quote/")
    : NAV_ITEMS;

  return (
    <nav
      className={`pd-header__nav${
        variant === "home" ? " pd-header__nav--home" : ""
      }`}
      aria-label="주요 메뉴"
    >
      <div className="pd-header__nav-inner">
        {navItems.map((item) => {
          // /region/parlor/… 하위 어디서든 상위 Nav 가 켜져 있어야 위치가 읽힌다
          const isActive = router.asPath.startsWith(item.href);

          return (
            <a
              key={item.href}
              href={item.href}
              className={`pd-header__nav-link${
                isActive ? " pd-header__nav-link--on" : ""
              }`}
              aria-current={isActive ? "page" : undefined}
              onClick={() =>
                customEvent({
                  newGtm: {
                    path: "prlr_header",
                    trigger_and_target: item.trigger,
                  },
                })
              }
            >
              {item.label}
              {"badge" in item && (
                <span className="pd-header__nav-badge" aria-hidden="true">
                  {item.badge}
                </span>
              )}
            </a>
          );
        })}
      </div>
    </nav>
  );
};
